import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const TermosPage = () => {
  const [activeSection, setActiveSection] = useState('reservas');

  const sections = [
    { id: 'reservas', label: 'Reservas e Pagamentos' },
    { id: 'cancelamento', label: 'Política de Cancelamento' },
    { id: 'checkin', label: 'Check-in & Check-out' },
    { id: 'regras', label: 'Regras da Casa' },
    { id: 'responsabilidades', label: 'Responsabilidades do Hóspede' },
    { id: 'danos', label: 'Danos e Depósito' },
    { id: 'limitacao', label: 'Limitação de Responsabilidade' },
    { id: 'lei', label: 'Lei Aplicável' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i].id);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white pt-32 pb-20">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLXdpZHRoPSIyIi8+PC9nPjwvc3ZnPg==')] bg-repeat"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-sea-blue/20 border-2 border-sea-blue">
              <i className="ri-file-text-line text-3xl text-sea-blue"></i>
            </div>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-center mb-6">
            Termos e Condições
          </h1>
          
          <p className="text-lg text-slate-300 text-center max-w-3xl mx-auto mb-8">
            Ao efetuar uma reserva nos nossos apartamentos, o hóspede concorda com os seguintes termos e condições. Leia atentamente antes de confirmar a sua estadia.
          </p>

          <div className="flex items-center justify-center space-x-2 text-sm text-slate-400">
            <i className="ri-calendar-line"></i>
            <span>Última atualização: 15 de janeiro de 2025</span>
          </div>
        </div>
      </section>

      {/* Navigation Index */}
      <div className="sticky top-20 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center space-x-1 overflow-x-auto py-4 scrollbar-hide">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer ${
                  activeSection === section.id
                    ? 'bg-sea-blue text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        {/* Reservas e Pagamentos */}
        <section id="reservas" className="mb-16 scroll-mt-32">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-bank-card-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                1. Reservas e Pagamentos
              </h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>1.1 Confirmação de Reserva:</strong> A reserva é considerada confirmada após o pagamento do sinal ou do valor total, conforme acordado. O hóspede receberá um email de confirmação com todos os detalhes da estadia.
            </p>
            <p>
              <strong>1.2 Métodos de Pagamento:</strong> Aceitamos pagamentos por transferência bancária, MBWay, cartão de crédito/débito ou plataformas de reserva autorizadas (Booking.com, Airbnb). O pagamento deve ser efetuado em euros (EUR).
            </p>
            <p>
              <strong>1.3 Sinal de Reserva:</strong> Para garantir a reserva, é necessário o pagamento de um sinal de 30% do valor total da estadia. O valor restante deve ser pago até 7 dias antes da data de check-in, salvo acordo diferente.
            </p>
            <p>
              <strong>1.4 Preços:</strong> Os preços apresentados incluem IVA à taxa legal em vigor. Não incluem taxas turísticas obrigatórias, que serão cobradas separadamente no momento do check-in (€2 por pessoa/noite, máximo 7 noites).
            </p>
            <p>
              <strong>1.5 Alterações de Preço:</strong> Os preços são garantidos no momento da confirmação da reserva. Reservas não confirmadas estão sujeitas a alterações de preço sem aviso prévio.
            </p>
          </div>
        </section>

        {/* Política de Cancelamento */}
        <section id="cancelamento" className="mb-16 scroll-mt-32">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-close-circle-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                2. Política de Cancelamento
              </h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>2.1 Cancelamento Gratuito:</strong> O hóspede pode cancelar a reserva sem custos até 14 dias antes da data de check-in. O valor pago será reembolsado na totalidade no prazo de 10 dias úteis.
            </p>
            <p>
              <strong>2.2 Cancelamento entre 7 e 14 dias:</strong> Cancelamentos efetuados entre 7 e 14 dias antes do check-in terão uma penalização de 50% do valor total da reserva.
            </p>
            <p>
              <strong>2.3 Cancelamento com menos de 7 dias:</strong> Cancelamentos efetuados com menos de 7 dias de antecedência ou no-show (não comparência) não terão direito a reembolso. O valor total da reserva será retido.
            </p>
            <p>
              <strong>2.4 Alterações de Datas:</strong> Alterações de datas estão sujeitas a disponibilidade e podem implicar ajustes de preço. Pedidos de alteração devem ser feitos por escrito (email ou WhatsApp) com pelo menos 7 dias de antecedência.
            </p>
            <p>
              <strong>2.5 Circunstâncias Excecionais:</strong> Em caso de força maior (doença grave comprovada, falecimento de familiar direto, cancelamento de voos), analisaremos cada situação individualmente. É necessária apresentação de documentação comprovativa.
            </p>
          </div>
        </section>

        {/* Check-in & Check-out */}
        <section id="checkin" className="mb-16 scroll-mt-32">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-key-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                3. Check-in & Check-out
              </h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>3.1 Horário de Check-in:</strong> O check-in é realizado entre as 15h00 e as 20h00. Check-ins fora deste horário devem ser solicitados com antecedência e estão sujeitos a disponibilidade e possível taxa adicional de €20.
            </p>
            <p>
              <strong>3.2 Horário de Check-out:</strong> O check-out deve ser efetuado até às 11h00. Late check-out (até às 14h00) pode ser solicitado com antecedência, sujeito a disponibilidade e taxa adicional de €15.
            </p>
            <p>
              <strong>3.3 Processo de Check-in:</strong> No momento do check-in, é obrigatória a apresentação de documento de identificação válido (Cartão de Cidadão, Passaporte ou Carta de Condução) de todos os hóspedes maiores de 18 anos, conforme legislação portuguesa.
            </p>
            <p>
              <strong>3.4 Entrega de Chaves:</strong> As chaves serão entregues pessoalmente ou através de sistema de lockbox, conforme combinado previamente. O hóspede é responsável pela segurança das chaves durante toda a estadia.
            </p>
            <p>
              <strong>3.5 Inspeção do Apartamento:</strong> Recomendamos que o hóspede inspecione o apartamento no momento do check-in e reporte imediatamente qualquer anomalia ou dano pré-existente.
            </p>
          </div>
        </section>

        {/* Regras da Casa */}
        <section id="regras" className="mb-16 scroll-mt-32">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-home-heart-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                4. Regras da Casa
              </h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>4.1 Ruído e Sossego:</strong> É obrigatório respeitar o sossego dos vizinhos. Ruído excessivo não é permitido entre as 22h00 e as 08h00. Festas e eventos não são autorizados sem consentimento prévio por escrito.
            </p>
            <p>
              <strong>4.2 Proibição de Fumar:</strong> É estritamente proibido fumar dentro dos apartamentos. Os hóspedes podem fumar nas varandas ou áreas exteriores designadas. O não cumprimento resultará numa taxa de limpeza adicional de €150.
            </p>
            <p>
              <strong>4.3 Animais de Estimação:</strong> Animais de estimação não são permitidos, salvo acordo prévio por escrito. Quando autorizados, aplica-se uma taxa adicional de €15 por noite e o hóspede é totalmente responsável por quaisquer danos causados pelo animal.
            </p>
            <p>
              <strong>4.4 Capacidade Máxima:</strong> O número de hóspedes não pode exceder a capacidade máxima indicada para cada apartamento. Hóspedes adicionais não autorizados resultarão em cobrança extra de €25 por pessoa/noite ou cancelamento imediato da reserva sem reembolso.
            </p>
            <p>
              <strong>4.5 Visitas:</strong> Visitas são permitidas entre as 10h00 e as 22h00, desde que não perturbem outros hóspedes ou vizinhos. Visitas não podem pernoitar sem autorização prévia e pagamento adicional.
            </p>
            <p>
              <strong>4.6 Uso de Equipamentos:</strong> Todos os equipamentos e eletrodomésticos devem ser utilizados de forma adequada e responsável. Instruções de uso estão disponíveis no apartamento. Em caso de dúvida, contacte-nos.
            </p>
          </div>
        </section>

        {/* Responsabilidades do Hóspede */}
        <section id="responsabilidades" className="mb-16 scroll-mt-32">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-user-star-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                5. Responsabilidades do Hóspede
              </h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>5.1 Cuidado com a Propriedade:</strong> O hóspede compromete-se a tratar o apartamento e todos os seus conteúdos com cuidado e respeito, como se fossem seus. Qualquer dano deve ser reportado imediatamente.
            </p>
            <p>
              <strong>5.2 Segurança:</strong> O hóspede é responsável por trancar portas e janelas sempre que sair do apartamento. Não nos responsabilizamos por perdas ou roubos de objetos pessoais. Recomendamos o uso do cofre disponível no apartamento.
            </p>
            <p>
              <strong>5.3 Consumos:</strong> Os consumos de água, eletricidade e gás estão incluídos no preço da estadia, desde que sejam razoáveis. Uso excessivo ou desperdício pode resultar em cobrança adicional.
            </p>
            <p>
              <strong>5.4 Limpeza:</strong> Esperamos que o hóspede mantenha o apartamento em condições razoáveis de limpeza durante a estadia. A limpeza final está incluída, mas não cobre situações de sujidade excessiva, que serão cobradas à parte.
            </p>
            <p>
              <strong>5.5 Separação de Resíduos:</strong> Pedimos a colaboração na separação de resíduos (papel, plástico, vidro, orgânico) conforme indicações disponíveis no apartamento. É uma questão de responsabilidade ambiental.
            </p>
          </div>
        </section>

        {/* Danos e Depósito */}
        <section id="danos" className="mb-16 scroll-mt-32">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-shield-check-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                6. Danos e Depósito de Segurança
              </h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>6.1 Depósito de Segurança:</strong> Pode ser solicitado um depósito de segurança de €200 no momento do check-in, que será devolvido no prazo de 7 dias após o check-out, desde que não existam danos ou faltas.
            </p>
            <p>
              <strong>6.2 Responsabilidade por Danos:</strong> O hóspede é totalmente responsável por quaisquer danos causados ao apartamento, mobiliário, equipamentos ou áreas comuns durante a sua estadia, incluindo danos causados por convidados ou visitantes.
            </p>
            <p>
              <strong>6.3 Avaliação de Danos:</strong> Após o check-out, será realizada uma inspeção do apartamento. Caso sejam identificados danos, o hóspede será notificado no prazo de 48 horas com descrição detalhada e orçamento de reparação.
            </p>
            <p>
              <strong>6.4 Pagamento de Danos:</strong> O custo de reparação ou substituição será deduzido do depósito de segurança. Se o valor exceder o depósito, o hóspede será contactado para pagamento do valor remanescente no prazo de 7 dias.
            </p>
            <p>
              <strong>6.5 Perda de Chaves:</strong> A perda de chaves ou comandos implica uma taxa de substituição de €50. A perda de cartões de acesso tem um custo de €30 por unidade.
            </p>
          </div>
        </section>

        {/* Limitação de Responsabilidade */}
        <section id="limitacao" className="mb-16 scroll-mt-32">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-information-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                7. Limitação de Responsabilidade
              </h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>7.1 Interrupções de Serviços:</strong> Não nos responsabilizamos por interrupções temporárias de serviços essenciais (água, eletricidade, internet) causadas por fatores externos ou manutenção de emergência. Faremos todos os esforços para resolver rapidamente.
            </p>
            <p>
              <strong>7.2 Objetos Pessoais:</strong> Não nos responsabilizamos pela perda, roubo ou dano de objetos pessoais, dinheiro ou valores deixados no apartamento. Recomendamos o uso de seguros de viagem.
            </p>
            <p>
              <strong>7.3 Acidentes Pessoais:</strong> O hóspede utiliza as instalações por sua conta e risco. Não nos responsabilizamos por acidentes pessoais, exceto em casos de negligência comprovada da nossa parte.
            </p>
            <p>
              <strong>7.4 Alterações nas Instalações:</strong> Reservamo-nos o direito de efetuar alterações, reparações ou melhorias nas instalações. Em caso de obras que afetem significativamente a estadia, o hóspede será notificado e poderá cancelar sem penalizações.
            </p>
            <p>
              <strong>7.5 Força Maior:</strong> Não seremos responsáveis pelo não cumprimento das nossas obrigações devido a circunstâncias de força maior (catástrofes naturais, pandemias, greves, atos governamentais, etc.).
            </p>
          </div>
        </section>

        {/* Lei Aplicável */}
        <section id="lei" className="mb-16 scroll-mt-32">
          <div className="flex items-start space-x-4 mb-6">
            <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-sea-blue/10 flex-shrink-0">
              <i className="ri-scales-line text-2xl text-sea-blue"></i>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                8. Lei Aplicável e Resolução de Conflitos
              </h2>
              <div className="h-1 w-16 bg-sea-blue rounded-full"></div>
            </div>
          </div>

          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              <strong>8.1 Lei Aplicável:</strong> Estes termos e condições são regidos pela lei portuguesa. Qualquer litígio será submetido aos tribunais portugueses competentes.
            </p>
            <p>
              <strong>8.2 Resolução Amigável:</strong> Em caso de conflito ou reclamação, encorajamos o contacto direto connosco para tentarmos resolver a situação de forma amigável e satisfatória para ambas as partes.
            </p>
            <p>
              <strong>8.3 Livro de Reclamações:</strong> Está disponível o Livro de Reclamações físico no apartamento e online em <a href="https://www.livroreclamacoes.pt" target="_blank" rel="noopener noreferrer" className="text-sea-blue hover:underline cursor-pointer">www.livroreclamacoes.pt</a>, conforme legislação portuguesa.
            </p>
            <p>
              <strong>8.4 Entidade de Resolução de Litígios:</strong> Para resolução alternativa de litígios de consumo, pode recorrer ao Centro de Arbitragem de Conflitos de Consumo da Madeira ou à plataforma europeia de resolução de litígios online.
            </p>
            <p>
              <strong>8.5 Alterações aos Termos:</strong> Reservamo-nos o direito de alterar estes termos e condições a qualquer momento. As alterações entrarão em vigor imediatamente após publicação no website. Reservas já confirmadas mantêm os termos vigentes no momento da reserva.
            </p>
          </div>
        </section>

        {/* Contact CTA */}
        <div className="bg-gradient-to-br from-sea-blue to-teal-600 rounded-2xl p-8 md:p-12 text-white text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/20 mx-auto mb-6">
            <i className="ri-question-line text-3xl"></i>
          </div>
          
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Tem Alguma Dúvida?
          </h3>
          
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Se tiver questões sobre os nossos termos e condições ou precisar de esclarecimentos adicionais, estamos aqui para ajudar.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://wa.me/351962050526?text=Olá!%20Tenho%20uma%20questão%20sobre%20os%20Termos%20e%20Condições"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 bg-white text-sea-blue px-8 py-4 rounded-lg font-semibold hover:bg-slate-50 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              <span>WhatsApp Direto</span>
            </a>

            <Link
              to="/contactos"
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all border-2 border-white/30 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-line text-xl"></i>
              <span>Enviar Email</span>
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermosPage;